import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

const EXCEL_MAX_COLS = 16384;
const EXCEL_MAX_ROWS = 1048576;
const EXCEL_SHEET_NAME_MAX = 31;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
export interface ExportSelection {
  year: number | null;
  months: number[];
  monthDates?: { [month: number]: number[] };
}

@Injectable({ providedIn: 'root' })
export class FormExcelExport {

  private readonly EXCLUDED_SECTION_NAMES = ['year & month', 'year and month'];

  exportTemplate(options: any): any {
    const warnings: string[] = [];

    try {
      const {
        formDefinition,
        selections,
        includeDate = false,
        fileName
      } = options ?? ({} as any);

      if (!formDefinition) {
        return { success: false, message: 'Form definition is required.' };
      }

      const blocks: ExportSelection[] =
        Array.isArray(selections) && selections.length
          ? selections.map((s: ExportSelection) => ({
            year: s?.year ?? null,
            months: Array.isArray(s?.months)
              ? [...new Set<number>(s.months.map(Number))]
                .filter(m => Number.isInteger(m) && m >= 1 && m <= 12)
                .sort((a, b) => a - b)
              : [],
            monthDates: s?.monthDates ?? {}
          }))
          : [{ year: null, months: [] }];

      const forms: any[] = Array.isArray(formDefinition?.data)
        ? formDefinition.data
        : formDefinition?.sections
          ? [formDefinition]
          : [];

      if (!forms.length) {
        return { success: false, message: 'No form definition found.' };
      }

      const form = forms[0];
      const allSections: any[] = Array.isArray(form.sections)
        ? [...form.sections].sort(
          (a, b) => (a.section_index_id ?? 0) - (b.section_index_id ?? 0)
        )
        : [];

      const sections = allSections.filter(sec => !this.shouldSkipSection(sec));

      if (!sections.length) {
        return {
          success: false,
          message: `Form "${form.form_name}" has no renderable (Matrix) sections.`
        };
      }

      const blockWidth = sections.reduce((max: number, s: any) => {
        const fields = Array.isArray(s.fields) ? s.fields : [];
        const matrixRows = Array.isArray(s.matrix_rows) ? s.matrix_rows : [];
        const hasAnyRemark = matrixRows.some((mr: any) => mr?.is_remark === true);

        const width = 1 + Math.max(fields.length, 1) + (hasAnyRemark ? 1 : 0);
        return Math.max(max, width);
      }, 2);

      const GAP = 1;
      const totalCols = blocks.length * blockWidth + (blocks.length - 1) * GAP;

      if (totalCols > EXCEL_MAX_COLS) {
        return {
          success: false,
          message: `Too many year columns (${blocks.length}). Excel supports only ${EXCEL_MAX_COLS} columns.`
        };
      }

      const ws: XLSX.WorkSheet = {};
      const merges: XLSX.Range[] = [];
      const rows: XLSX.RowInfo[] = [];
      const cols: XLSX.ColInfo[] = [];

      const title = fileName || `${form.form_name || 'Form'} Template`;
      this.writeMergedRow(
        ws, 0, 0, totalCols, title,
        {
          font: { sz: 16, bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '8D1D75' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: this.mediumBorder()
        },
        30, merges, rows
      );

      const BODY_START_ROW = 2;
      let maxRow = BODY_START_ROW;
      let monthsExported = 0;
      let datesExported = 0;
      let sectionsExported = 0;
      let limitReached = false;

      for (let b = 0; b < blocks.length && !limitReached; b++) {
        const block = blocks[b];
        const startCol = b * (blockWidth + GAP);
        let r = BODY_START_ROW;

        if (block.year !== null) {
          this.writeMergedRow(
            ws, r, startCol, blockWidth,
            `Year - ${block.year}`,
            {
              font: { sz: 14, bold: true, color: { rgb: 'FFFFFF' } },
              fill: { fgColor: { rgb: '8D1D75' } },
              alignment: { horizontal: 'center', vertical: 'center' },
              border: this.mediumBorder()
            },
            26, merges, rows
          );
          r++;
        }

        const monthList: { month: number | null; date: number | null }[] = [];
        if (block.months.length) {
          for (const m of block.months) {
            const picked: number[] = includeDate
              ? [...new Set<number>((block.monthDates?.[m] ?? []).map(Number))]
                .filter(d => Number.isInteger(d) && d >= 1 && d <= 31)
                .sort((a, b) => a - b)
              : [];
            if (picked.length) {
              picked.forEach(d => monthList.push({ month: m, date: d }));
            } else {
              monthList.push({ month: m, date: null });
            }
          }
        } else {
          monthList.push({ month: null, date: null });
        }

        for (const { month, date } of monthList) {

          if (month !== null) {
            this.writeMergedRow(
              ws, r, startCol, blockWidth,
              `Month - ${MONTH_NAMES[month - 1] ?? month}`,
              {
                font: { sz: 13, bold: true, color: { rgb: '8D1D75' } },
                fill: { fgColor: { rgb: 'FFF3CD' } },
                alignment: { horizontal: 'center', vertical: 'center' },
                border: this.thinBorder()
              },
              22, merges, rows
            );
            r++;
            monthsExported++;
          }

          if (includeDate) {
            this.writeMergedRow(
              ws, r, startCol, blockWidth,
              date !== null ? `Date - ${date}` : 'Date -',
              {
                font: { sz: 12, italic: true, color: { rgb: '8D1D75' } },
                fill: { fgColor: { rgb: 'F3E8F6' } },
                alignment: { horizontal: 'left', vertical: 'center' },
                border: this.thinBorder()
              },
              20, merges, rows
            );
            r++;
            datesExported++;
          }

          for (const section of sections) {
            try {
              r = this.renderSection(ws, r, startCol, blockWidth, section, merges, rows);
              sectionsExported++;

              if (r > EXCEL_MAX_ROWS) {
                warnings.push('Row limit reached. Stopping early.');
                limitReached = true;
                break;
              }
            } catch (secErr) {
              warnings.push(
                `Failed rendering section "${section?.section_name}": ${String(secErr)}`
              );
            }
          }
          if (limitReached) break;

          if (month !== null) r++;
        }

        maxRow = Math.max(maxRow, r);
      }

      for (let b = 0; b < blocks.length; b++) {
        cols.push({ wch: 28 }); 
        for (let c = 1; c < blockWidth; c++) {
          cols.push({ wch: c === blockWidth - 1 ? 30 : 14 });
        }
        if (b < blocks.length - 1) cols.push({ wch: 3 });
      }

      ws['!merges'] = merges;
      ws['!rows'] = rows;
      ws['!cols'] = cols;
      ws['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: Math.max(maxRow - 1, 0), c: totalCols - 1 }
      });

      let buffer: any;
      try {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          wb, ws, this.sanitizeSheetName(form.form_name || 'Template')
        );
        buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      } catch (e) {
        return {
          success: false,
          message: `Failed to write workbook: ${String(e)}`,
          error: e
        };
      }

      const safeFileName = this.sanitizeFileName(
        fileName || `${form.form_name || 'form'}_template`
      );

      try {
        saveAs(
          new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }),
          safeFileName
        );
      } catch (e) {
        return {
          success: false,
          message: `Failed to save file: ${String(e)}`,
          error: e
        };
      }

      return {
        success: true,
        message: 'Template exported successfully.',
        stats: {
          yearsExported: blocks.filter(x => x.year !== null).length,
          monthsExported,
          datesExported,
          sectionsExported,
          rowsWritten: maxRow,
          warnings
        }
      };

    } catch (err) {
      console.error('[FormExcelExport] Unexpected:', err);
      return {
        success: false,
        message: `Unexpected error: ${err instanceof Error ? err.message : String(err)}`,
        error: err
      };
    }
  }

  private shouldSkipSection(section: any): boolean {
    if (!section) return true;

    const layout = (section.layout_type || '').trim().toLowerCase();
    if (layout !== 'matrix') return true;

    const name = (section.section_name || '').trim().toLowerCase();
    if (this.EXCLUDED_SECTION_NAMES.includes(name)) return true;

    if (!Array.isArray(section.matrix_rows) || section.matrix_rows.length === 0) {
      return true;
    }
    return false;
  }

  private setRowHeight(rows: XLSX.RowInfo[], row: number, hpt: number) {
    const current = rows[row]?.hpt ?? 0;
    rows[row] = { hpt: Math.max(current, hpt) };
  }

  private renderSection(
    ws: XLSX.WorkSheet,
    startRow: number,
    startCol: number,
    blockWidth: number,
    section: any,
    merges: XLSX.Range[],
    rows: XLSX.RowInfo[]
  ): number {
    let r = startRow;

    const fields = Array.isArray(section.fields) ? section.fields : [];
    const matrixRows = Array.isArray(section.matrix_rows) ? section.matrix_rows : [];

    const hasAnyRemark = matrixRows.some((mr: any) => mr?.is_remark === true);

    const fieldCellCount = Math.max(fields.length, 1);
    const sectionWidth = Math.min(1 + fieldCellCount + (hasAnyRemark ? 1 : 0), blockWidth);

    this.writeMergedRow(
      ws, r, startCol, sectionWidth,
      section.section_name?.toUpperCase() ?? 'SECTION',
      {
        font: { sz: 12, bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'A94C8F' } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: this.mediumBorder()
      },
      22, merges, rows
    );
    r++;

    const headerCells: string[] = [];
    headerCells.push(
      section.is_row_label ? `${section.section_name} Details` : 'Particulars'
    );
    fields.forEach((f: any) =>
      headerCells.push(f.field_label ?? f.field_key ?? 'Field')
    );
    if (hasAnyRemark) headerCells.push('Remark');

    for (let c = 0; c < sectionWidth; c++) {
      const v = headerCells[c] ?? '';
      this.writeCell(ws, r, startCol + c, v, {
        font: { sz: 11, bold: true, color: { rgb: '8D1D75' } },
        fill: { fgColor: { rgb: 'E6CFEB' } },
        alignment: { horizontal: c === 0 ? 'left' : 'center', vertical: 'center' },
        border: this.thinBorder()
      });
    }
    this.setRowHeight(rows, r, 20);
    r++;

    for (let i = 0; i < matrixRows.length; i++) {
      const matrixRow = matrixRows[i];
      const label = matrixRow?.master_value_name ?? '';

      this.writeCell(ws, r, startCol, label, {
        font: { sz: 11, color: { rgb: '000000' } },
        fill: { fgColor: { rgb: 'FFFFFF' } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: this.thinBorder()
      });

      for (let c = 1; c <= fieldCellCount; c++) {
        this.writeCell(ws, r, startCol + c, '', {
          font: { sz: 11, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: this.thinBorder()
        });
      }

      if (hasAnyRemark) {
        const remarkCol = startCol + 1 + fieldCellCount;
        this.writeCell(ws, r, remarkCol, '', {
          font: { sz: 11, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'left', vertical: 'center' },
          border: this.thinBorder()
        });
      }

      this.setRowHeight(rows, r, 18);
      r++;
    }

    r++; // spacer between sections
    return r;
  }

  private writeMergedRow(
    ws: XLSX.WorkSheet,
    row: number,
    startCol: number,
    colSpan: number,
    text: string,
    style: any,
    heightPt: number,
    merges: XLSX.Range[],
    rows: XLSX.RowInfo[]
  ) {
    for (let k = 0; k < colSpan; k++) {
      this.writeCell(ws, row, startCol + k, k === 0 ? text : '', style);
    }
    if (colSpan > 1) {
      merges.push({
        s: { r: row, c: startCol },
        e: { r: row, c: startCol + colSpan - 1 }
      });
    }
    this.setRowHeight(rows, row, heightPt);
  }

  private writeCell(
    ws: XLSX.WorkSheet,
    row: number,
    col: number,
    value: any,
    style: any
  ) {
    const addr = XLSX.utils.encode_cell({ r: row, c: col });
    ws[addr] = {
      v: value,
      s: style,
      t: typeof value === 'number' ? 'n' : 's'
    };
  }

  private thinBorder() {
    return {
      top: { style: 'thin', color: { rgb: 'B8A8BC' } },
      bottom: { style: 'thin', color: { rgb: 'B8A8BC' } },
      left: { style: 'thin', color: { rgb: 'B8A8BC' } },
      right: { style: 'thin', color: { rgb: 'B8A8BC' } }
    };
  }

  private mediumBorder() {
    return {
      top: { style: 'medium', color: { rgb: '8D1D75' } },
      bottom: { style: 'medium', color: { rgb: '8D1D75' } },
      left: { style: 'medium', color: { rgb: '8D1D75' } },
      right: { style: 'medium', color: { rgb: '8D1D75' } }
    };
  }

  private sanitizeFileName(name: string): string {
    let cleaned = String(name || 'template')
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) cleaned = 'template';
    if (!/\.xlsx$/i.test(cleaned)) cleaned += '.xlsx';
    if (cleaned.length > 200) cleaned = cleaned.substring(0, 200);
    return cleaned;
  }

  private sanitizeSheetName(name: string): string {
    let cleaned = String(name || 'Form')
      .replace(/[\\/:*?\[\]]/g, '_')
      .trim();

    if (!cleaned) cleaned = 'Form';
    return cleaned.substring(0, EXCEL_SHEET_NAME_MAX);
  }
}